/**
 * UTKAST (AI-ANALYSE D9, 279.10 a): en melding eller et bud som er skrevet, men ikke sendt.
 *
 * =================================================================================================
 * HVORFOR DE FINNES. En AI får bare lage UTKAST til det som går til en annen organisasjon — bud og
 * forespørsler til et byrå, meldinger i tråder motparten leser. Utkastet lagres, ses bare av
 * forfatteren, og sendes ALDRI av seg selv: brukeren ser over og sender i Tvitvi, med en vanlig
 * sesjon (`draft:send` er sperret for tokens utenfra, `external-access.ts`).
 *
 * =================================================================================================
 * MÅLET ER DET SAMME SOM DE EKTE RUTENE. `target.id` er trådens id, programpostens, artistens i
 * registeret eller engasjementets — nøyaktig nøkkelen `POST /threads/:id/messages`,
 * `/performance-chat/messages`, `/booking-chat/messages` og `/engagement-chat/messages` tar. Et bud
 * er hele `CreateBookingBidRequest`, og målet er artisten budet gjelder (`booking_artist`).
 *
 * Innholdet valideres med de ekte rutenes skjemaer både når utkastet lagres og når det sendes, så
 * et utkast som ble lagret, kan sendes — eller får den samme 400 som nettsiden ville fått.
 */
import { z } from 'zod';

import { page, pageQuerySchema } from '../http/pagination.js';
import { createBookingBidRequestSchema } from './booking-register.js';
import { MAX_MESSAGE_MENTIONS } from './inbox.js';

export const DRAFT_KINDS = ['message', 'booking_bid'] as const;
export const draftKindSchema = z.enum(DRAFT_KINDS).meta({ id: 'DraftKind' });
export type DraftKind = z.infer<typeof draftKindSchema>;

/** Hvor en melding skal: de fire samtaleflatene som tar en melding fra nettsiden. */
export const DRAFT_MESSAGE_TARGETS = [
  'thread',
  'performance_chat',
  'booking_chat',
  'engagement_chat',
] as const;
export type DraftMessageTargetType = (typeof DRAFT_MESSAGE_TARGETS)[number];

/** Alle målene, budets artist med. Det er denne lista `GET /drafts?targetType=` filtrerer på. */
export const DRAFT_TARGET_TYPES = [...DRAFT_MESSAGE_TARGETS, 'booking_artist'] as const;
export const draftTargetTypeSchema = z.enum(DRAFT_TARGET_TYPES).meta({ id: 'DraftTargetType' });
export type DraftTargetType = z.infer<typeof draftTargetTypeSchema>;

export const draftMessageTargetSchema = z
  .object({ type: z.enum(DRAFT_MESSAGE_TARGETS), id: z.uuid() })
  .meta({ id: 'DraftMessageTarget' });

export const draftTargetSchema = z
  .object({ type: draftTargetTypeSchema, id: z.uuid() })
  .meta({ id: 'DraftTarget' });
export type DraftTarget = z.infer<typeof draftTargetSchema>;

/** Den samme grensen som en melding har (`createMessageRequestSchema.body`). */
const draftBodySchema = z.string().trim().min(1).max(20_000);

/**
 * To former på ledningen, ett skjema:
 *
 *   { kind: 'message', target: { type, id }, body, mentionPersonIds?, inReplyToMessageId? }
 *   { kind: 'booking_bid', payload: CreateBookingBidRequest }
 *
 * ET OBJEKT MED EN SJEKK, IKKE EN UNION, fordi kroppen er en DTO-klasse (`createZodDto`), og en klasse
 * kan ikke ha en union som instans. `superRefine` sier det samme som unionen ville sagt: feltene som
 * ikke hører til slaget, avvises.
 */
export const createDraftRequestSchema = z
  .object({
    kind: draftKindSchema,
    target: draftMessageTargetSchema.optional(),
    body: draftBodySchema.optional(),
    mentionPersonIds: z.array(z.uuid()).max(MAX_MESSAGE_MENTIONS).optional(),
    inReplyToMessageId: z.uuid().nullish(),
    payload: createBookingBidRequestSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.kind === 'message') {
      if (value.target === undefined) {
        ctx.addIssue({ code: 'custom', path: ['target'], message: 'required_for_message' });
      }
      if (value.body === undefined) {
        ctx.addIssue({ code: 'custom', path: ['body'], message: 'required_for_message' });
      }
      if (value.payload !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['payload'], message: 'not_allowed_for_message' });
      }
      return;
    }
    if (value.payload === undefined) {
      ctx.addIssue({ code: 'custom', path: ['payload'], message: 'required_for_booking_bid' });
    }
    for (const key of ['target', 'body', 'mentionPersonIds', 'inReplyToMessageId'] as const) {
      if (value[key] !== undefined && value[key] !== null) {
        ctx.addIssue({ code: 'custom', path: [key], message: 'not_allowed_for_booking_bid' });
      }
    }
  })
  .meta({ id: 'CreateDraftRequest' });
export type CreateDraftRequest = z.infer<typeof createDraftRequestSchema>;

export const DRAFT_CLIENT_TYPES = ['ai', 'integration'] as const;

export const draftDtoSchema = z
  .object({
    id: z.uuid(),
    kind: draftKindSchema,
    target: draftTargetSchema,
    /** Meldingens tekst. Null for et bud. */
    body: z.string().nullable(),
    mentionPersonIds: z.array(z.uuid()),
    inReplyToMessageId: z.uuid().nullable(),
    /** Budet slik det skal sendes. Null for en melding. */
    bid: createBookingBidRequestSchema.nullable(),
    /** Klienten som laget det (`Claude`, `ChatGPT` …), fra tilkoblingen. Null når brukeren selv gjorde det. */
    clientType: z.enum(DRAFT_CLIENT_TYPES).nullable(),
    clientName: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'Draft' });
export type DraftDto = z.infer<typeof draftDtoSchema>;

/** Utkastene er få per person; lista er paget som alle andre, nyeste først. */
export const listDraftsQuerySchema = pageQuerySchema(50, 100)
  .extend({
    kind: draftKindSchema.optional(),
    /** Komponisten spør om sin egen samtale: `targetType` og `targetId` sammen. */
    targetType: draftTargetTypeSchema.optional(),
    targetId: z.uuid().optional(),
  })
  .meta({ id: 'ListDraftsQuery' });
export type ListDraftsQuery = z.infer<typeof listDraftsQuerySchema>;

export const draftListResponseSchema = page(draftDtoSchema, 'DraftListResponse');
export type DraftListResponse = z.infer<typeof draftListResponseSchema>;

/**
 * Å SENDE: det brukeren rettet før hen trykket «Send». Alt er valgfritt — et utkast kan sendes
 * slik det står. Feltene som ikke hører til utkastets slag, avvises (400).
 */
export const sendDraftRequestSchema = z
  .object({
    body: draftBodySchema.optional(),
    mentionPersonIds: z.array(z.uuid()).max(MAX_MESSAGE_MENTIONS).optional(),
    inReplyToMessageId: z.uuid().nullish(),
    /** Tråden: ta de navngitte inn i tråden (samme flagg som `POST /threads/:id/messages`). */
    addMentioned: z.boolean().optional(),
    /** Budet slik brukeren rettet det. Erstatter hele det lagrede. */
    bid: createBookingBidRequestSchema.optional(),
  })
  .meta({ id: 'SendDraftRequest' });
export type SendDraftRequest = z.infer<typeof sendDraftRequestSchema>;

export const sendDraftResponseSchema = z
  .object({
    draftId: z.uuid(),
    kind: draftKindSchema,
    sentAt: z.iso.datetime(),
    /** Det som ble laget: meldingen eller budet, med id-en klienten trenger (vedlegg, dyplenke). */
    sent: z.object({
      objectType: z.enum(['message', 'booking_bid']),
      objectId: z.uuid(),
    }),
  })
  .meta({ id: 'SendDraftResponse' });
export type SendDraftResponse = z.infer<typeof sendDraftResponseSchema>;
